import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useReactiveVar } from '@apollo/client'

import { useLoading, useServerError } from 'shared/hooks'
import { currentUserVar, notificationMessageVar } from 'modulesCommon/store'
import { useLocalize } from 'modules/Globalize/graphql/mutations/localizeProject'
import { useGlobalProjectFlags, useZonesList } from 'modulesCommon/serviceHooks'

const useLocalizer = ({ project }: { project: any }) => {
  const navigate = useNavigate()
  const currentUser = useReactiveVar(currentUserVar)
  const { zoneList, zoneError, zoneLoading } = useZonesList()

  const { isLocalized, isLocalizing, localizedProjectInUserZone } = useGlobalProjectFlags({
    project,
    userZoneId: +currentUser?.label_id
  })

  const { mutate: localizeProject, loading: localizeProjectLoading } = useLocalize()

  const label = useMemo(
    () => zoneList.find((label) => `${label?.id}` === `${currentUser?.label_id}`)?.alias,
    [zoneList, currentUser?.label_id]
  )

  const localizeButtonView = useMemo(
    () =>
      isLocalized
        ? {
          label: `View Project in ${label}`,
          secondaryIcon: 'arrow-up-right-from-square'
        }
        : {
          label: isLocalizing ? 'Continue Localizing' : 'Localize Project'
        },
    [isLocalized, isLocalizing, label]
  )

  const goToLocalizeStepper = useCallback(
    (id: string | undefined) => id && navigate(`/globalhub/projects/localize/${id}`),
    [navigate]
  )
  const goToProject = useCallback(
    (id: string | undefined) => id && navigate(`/projects/${id}`, { state: { isLocalized: true }}),
    [navigate]
  )

  const createLocalize = useCallback(() => {
    localizeProject({
      variables: { input: { parent_id: project?.id, label_id: currentUser?.label_id } }
    })
      .then(({ data: { localizeCampaign } }) => {
        goToLocalizeStepper(localizeCampaign?.id)
      })
      .catch((error) => {
        notificationMessageVar({
          type: 'danger',
          message: error?.message,
          title: 'Warning'
        })
      })
  }, [project?.id, currentUser?.label_id, goToLocalizeStepper, localizeProject])

  const onLocalize = useCallback(async () => {
    if (isLocalizing) {
      return goToLocalizeStepper(localizedProjectInUserZone?.id)
    }
    if (isLocalized) {
      return goToProject(localizedProjectInUserZone?.id)
    }
    await createLocalize()
  }, [goToLocalizeStepper, isLocalized, createLocalize, goToProject, localizedProjectInUserZone, isLocalizing])

  const onLocalizeProject = useCallback(() => {
    onLocalize()
  }, [onLocalize])

  useLoading({
    isLoading: localizeProjectLoading || zoneLoading
  })

  useServerError([zoneError])

  return {
    localizeButtonView,
    onLocalizeProject,
    isButtonVisible: project?.active
  }
}

export default useLocalizer
